import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { VideoPlayerSyncContext } from './VideoPlayerSyncContext';
import { React } from 'Imports';

interface VideoPlayerSyncProviderProps {
    children: React.ReactNode;
    videoEventId?: number;
    videoEventDisplayId?: string;
    isFullView: boolean;
    disableSync?: boolean;
}

const VideoPlayerSyncProvider = (props: VideoPlayerSyncProviderProps) => {
    const [isPlayingGlobal, setIsPlayingGlobal] = useState<boolean>(false);
    const [maxDurationGlobal, setMaxDurationGlobal] = useState<number>(0);
    const [seekTimeGlobal, setSeekTimeGlobal] = useState<number>(0);
    const [videoEventId, setVideoEventId] = useState<number>(props.videoEventId ?? 0);
    const [videoEventDisplayId, setVideoEventDisplayId] = useState<string>(props.videoEventDisplayId ?? '');
    const [isFullView, setIsFullView] = useState<boolean>(props.isFullView ?? false);
    const [disableSync, setDisableSync] = useState<boolean>(props.disableSync ?? false);
    const [videosStarted, setVideosStarted] = useState<boolean>(false);
    const [videosWatched, setVideosWatched] = useState<boolean>(false);
    const [maxDurationVideoKey, setMaxDurationVideoKey] = useState<string>('');
    const [videosEnded, setVideosEnded] = useState<{ videoKey: string; ended: boolean }[]>([]);
    const [startPlayFromBeginning, setStartPlayFromBeginning] = useState<boolean>(false);

    const calculateMaxDuration = (duration: number, videoKey: string) => {
        if (duration > maxDurationGlobal) {
            setMaxDurationGlobal(duration);
            setMaxDurationVideoKey(videoKey);
        }
    };

    const createSyncedSetter = useCallback(
        <T,>(setter: Dispatch<SetStateAction<T>>) => {
            return (value: SetStateAction<T>) => {
                // sync only when not in full view to prevent unwanted syncing during fullscreen playback
                if (!disableSync) {
                    setter(value);
                }
            };
        },
        [disableSync],
    );

    const setVideoEnded = useCallback((videoKey: string, ended: boolean) => {
        setVideosEnded((prev) => {
            const existingIndex = prev.findIndex((v) => v.videoKey === videoKey);
            if (existingIndex !== -1) {
                if (prev[existingIndex].ended === ended) return prev;
                const updated = [...prev];
                updated[existingIndex] = { videoKey, ended };
                return updated;
            }
            return [...prev, { videoKey, ended }];
        });
    }, []);

    const allVideosEnded = (): boolean => {
        return videosEnded.length > 0 && videosEnded.every((v) => v.ended);
    };

    return (
        <VideoPlayerSyncContext.Provider
            value={{
                isPlayingGlobal,
                setIsPlayingGlobal: createSyncedSetter(setIsPlayingGlobal),
                seekTimeGlobal,
                setSeekTimeGlobal: createSyncedSetter(setSeekTimeGlobal),
                maxDurationGlobal,
                setMaxDurationGlobal: calculateMaxDuration,
                maxDurationVideoKey,
                videoEventId,
                setVideoEventId,
                videoEventDisplayId,
                setVideoEventDisplayId,
                isFullView,
                setIsFullView,
                disableSync,
                setDisableSync,
                videosStarted,
                setVideosStarted,
                videosWatched,
                setVideosWatched,
                videosEnded,
                setVideoEnded,
                allVideosEnded,
                startPlayFromBeginning,
                setStartPlayFromBeginning,
            }}
        >
            {props.children}
        </VideoPlayerSyncContext.Provider>
    );
};

export default VideoPlayerSyncProvider;
